import React, { useState, useEffect } from "react";
import { Table, Button, Space, Tag, message, Input, Skeleton } from "antd";
import { EyeOutlined, FileTextOutlined, DeleteOutlined, SearchOutlined, ShareAltOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import ButtonBar from "../../components/ButtonNavigation";
import { getAllReportsFromAllProjects, deleteReport, updateReportShareSettings } from "../../services/reportsService";
import ShareReportModal from "./reports/modals/ShareReportModal";

const PhotoReportsList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state?.User?.userDetails);
  const isDealer = currentUser?.user_role === 'um_trusted-dealer';
  const isInstaller = currentUser?.user_role === 'um_installer';
  const isTeamRole = isDealer || isInstaller;

  const [reports, setReports] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [layoutOptions, setLayoutOptions] = useState({ 2: true, 3: true, 4: true, 6: true });
  const [loadingAll, setLoadingAll] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [syncingToken, setSyncingToken] = useState(false);

  // Generate random token
  const generateShareToken = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 12; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleOpenShare = async (report) => {
    setSelectedReport(report);
    setShareModalVisible(true);
    
    if (!report.share_token) {
        setSyncingToken(true);
        const newToken = generateShareToken();
        try {
            await dispatch(updateReportShareSettings(report.id, {
                share_token: newToken,
                share_token_expires_at: null 
            }));
            // Update local state temporarily so the modal re-renders with token
            setSelectedReport({ ...report, share_token: newToken });
            // Update main list
            setAllReports(prev => prev.map(r => r.id === report.id ? { ...r, share_token: newToken } : r));
            setReports(prev => prev.map(r => r.id === report.id ? { ...r, share_token: newToken } : r));
        } catch (err) {
            console.error(err);
            message.error("Link sync failed. Please try 'Generate New Link'.");
        } finally {
            setSyncingToken(false);
        }
    }
  };

  // Add responsive styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media (max-width: 768px) {
        .photo-report-list-section {
          padding: 0 !important;
        }
        .project-photo-reports-head {
          padding: 12px !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    loadAllReports();
  }, []);

  const loadAllReports = async () => {
    setLoading(true);
    setLoadingAll(true);
    try {
      let allData = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await dispatch(getAllReportsFromAllProjects(currentPage));
        const reportsData = Array.isArray(response) ? response : (response.data || []);
        allData = [...allData, ...reportsData];
        
        // Check if there are more pages
        const total = response.total || reportsData.length;
        const perPage = response.per_page || 15;
        hasMore = currentPage * perPage < total;
        currentPage++;
      }

      setAllReports(allData);
      setReports(allData);
    } catch (error) {
      message.error('Failed to load reports');
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
      setLoadingAll(false);
    }
  };

  const filteredReports = searchText 
    ? allReports.filter(report => 
        report.title?.toLowerCase().includes(searchText.toLowerCase()) ||
        report.project?.id?.toString().includes(searchText) ||
        report.project?.name?.toLowerCase().includes(searchText.toLowerCase())
      )
    : allReports;

  const handleDeleteReport = async (reportId, reportTitle) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete the report "${reportTitle}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await dispatch(deleteReport(reportId));
          message.success('Report deleted successfully!');
          loadAllReports();
        } catch (error) {
          message.error('Failed to delete report');
          console.error('Error deleting report:', error);
        }
      }
    });
  };

  const columns = [
    {
      title: 'Report Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => loading ? <Skeleton.Input active size="small" /> : <strong>{text}</strong>,
    },
    {
      title: 'Project Name',
      dataIndex: 'project',
      key: 'project',
      render: (project, record) => loading ? <Skeleton.Input active size="small" /> : (
        <div>
          <div style={{ fontWeight: 500, color: '#1e293b' }}>{project?.name}</div>
          {isTeamRole && (
            <div style={{ marginTop: 4 }}>
              {record.project_is_owner ? (
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#0369a1',
                    background: '#e0f2fe',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  🏷️ Created by You
                </span>
              ) : isInstaller ? (
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#059669',
                    background: '#ecfdf5',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  🏢 Assigned by Dealer
                </span>
              ) : (
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#4338ca',
                    background: '#eef2ff',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  👷 {record.project_creator_name || "Installer"}
                </span>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Layout',
      dataIndex: 'photos_per_page',
      key: 'photos_per_page',
      render: (photos) => {
        if (loading) return <Skeleton.Button active size="small" />;
        const isValidLayout = layoutOptions[photos];
        return (
          <Tag color={isValidLayout ? "blue" : "orange"}>
            {photos} Photos per Page
          </Tag>
        );
      },
    },
    {
      title: 'Images',
      dataIndex: 'photos_count',
      key: 'photos_count',
      render: (count) => loading ? <Skeleton.Input active size="small" /> : <span>{count || 0} images</span>,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => loading ? <Skeleton.Input active size="small" /> : new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => loading ? (
        <Space>
          <Skeleton.Button active size="small" />
          <Skeleton.Button active size="small" />
          <Skeleton.Button active size="small" />
          <Skeleton.Button active size="small" />
        </Space>
      ) : (
        <Space wrap size="small">
          <Button
          className="btnIcon"
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/project-photo-report/${record.id}`, { state: { report: record } })}
            size="small"
          >
            View Report
          </Button>
          <Button
          className="btnIcon"
          icon={<EyeOutlined />}
            onClick={() => navigate(`/project-detail/?project_id=${record.project?.id}`)}
            size="small"
          >
            View Project
          </Button>
          <Button
            className="btnIcon"
            icon={<ShareAltOutlined />}
            onClick={() => handleOpenShare(record)}
            size="small"
          >
            Share Report
          </Button>
          <Button
            className="btnIcon"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteReport(record.id, record.title)}
            size="small"
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="container-xxl">
        <div className="button-navigations mt-5">
          <ButtonBar />
        </div>

        <div className="mt-4 mb-4 photo-report-list-section">
          <div className="project-photo-reports-head" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="d-flex justify-content-start flex-column w-100">
              <h2 className="project-photo-reports" style={{ margin: 0 }}>
                <FileTextOutlined /> Project Photo Reports
              </h2>
              <p style={{ color: '#888', margin: '8px 0 0 0' }}>
                All project photo reports
              </p>
            </div>
            <Space wrap style={{ width: '100%', justifyContent: 'end' }}>
              <Input
                placeholder="Search by title or project ID"
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ minWidth: '200px', maxWidth: '300px', flex: 1 }}
                allowClear
              />
              <Button type="primary" onClick={() => navigate('/projects')}>
                Go to Projects
              </Button>
            </Space>
          </div>

          <Table
          className="photoreportListTable "
            columns={columns}
            dataSource={loading ? Array(10).fill({}) : filteredReports}
            rowKey={(record, index) => record.id || index}
            loading={loadingAll}
            scroll={{ x: 800 }}
            pagination={loading ? false : {
              defaultPageSize: 10,
              pageSizeOptions: ['10', '20', '50', '100'],
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} reports`,
              responsive: true,
            }}
            locale={{
              emptyText: 'No photo reports created yet. Create a report from a project.',
            }}
          />
        </div>
      </div>

      <ShareReportModal 
          visible={shareModalVisible}
          onCancel={() => {
              setShareModalVisible(false);
              setSelectedReport(null);
          }}
          shareLink={selectedReport?.share_token ? `${window.location.origin}/shared-report/${selectedReport.share_token}` : ''}
          isSyncing={syncingToken}
          onGenerateNewLink={async () => {
              if (!selectedReport) return;
              const newToken = generateShareToken();
              setSyncingToken(true);
              try {
                  await dispatch(updateReportShareSettings(selectedReport.id, {
                      share_token: newToken,
                      share_token_expires_at: null 
                  }));
                  setSelectedReport({ ...selectedReport, share_token: newToken });
                  setAllReports(prev => prev.map(r => r.id === selectedReport.id ? { ...r, share_token: newToken } : r));
                  setReports(prev => prev.map(r => r.id === selectedReport.id ? { ...r, share_token: newToken } : r));
                  message.success("New secure link generated and saved!");
              } catch (error) {
                  message.error("Failed to update share link on server.");
              } finally {
                  setSyncingToken(false);
              }
          }}
          onViewReport={() => {
              if (selectedReport?.share_token) {
                  window.open(`/shared-report/${selectedReport.share_token}`, '_blank');
              }
          }}
          reportTitle={selectedReport?.title}
      />
    </div>
  );
};

export default PhotoReportsList;
